"use client";

import { useState } from "react";
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";

/**
 * Czytnik PDF dla publicznej strony dokumentu prawnego. Wykorzystuje wbudowaną
 * przeglądarkę PDF (iframe), a jako fallback (np. część przeglądarek mobilnych)
 * udostępnia przyciski pobrania i otwarcia w nowej karcie.
 */
export default function LegalPdfViewer({ url, name }: { url: string; name?: string }) {
  const [loaded, setLoaded] = useState(false);
  const fileName = name?.trim() || "dokument.pdf";

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
      {/* Pasek narzędzi */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-3 bg-slate-50 dark:bg-slate-800/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{fileName}</p>
            <p className="text-xs text-slate-400">Dokument PDF</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700/60 transition"
          >
            <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Otwórz</span>
          </a>
          <a
            href={url}
            download={fileName}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition"
          >
            <Download className="w-4 h-4" /> Pobierz PDF
          </a>
        </div>
      </div>

      {/* Podgląd PDF */}
      <div className="relative bg-slate-100 dark:bg-slate-950" style={{ height: "min(80vh, 900px)" }}>
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        <iframe
          src={`${url}#view=FitH`}
          title={fileName}
          className="w-full h-full"
          onLoad={() => setLoaded(true)}
        />
      </div>

      {/* Fallback dla urządzeń bez wbudowanego podglądu PDF */}
      <div className="sm:hidden border-t border-slate-100 dark:border-slate-800 px-4 py-3 text-center">
        <p className="text-xs text-slate-400 mb-2">Podgląd nie działa na Twoim urządzeniu?</p>
        <a
          href={url}
          download={fileName}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition"
        >
          <Download className="w-4 h-4" /> Pobierz dokument PDF
        </a>
      </div>
    </div>
  );
}
